import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api/Api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ── Wishlist stays local ──
  const [wishlistItems, setWishlistItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wishlist') || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem('wishlist', JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // ══════════════════════════════════════
  // 1. GET — fetch cart list
  // ══════════════════════════════════════
  const fetchCart = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    setIsLoading(true);
    try {
      const { data } = await api.get('cart/list');
      if (data.status) {
        setCartItems(data.data ?? []);
      }
    } catch (err) {
      console.error('fetchCart error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  // ══════════════════════════════════════
  // 2. ADD to cart
  // ══════════════════════════════════════
  const addToCart = async (productId, quantity = 1) => {
    try {
      const { data } = await api.post(
        'cart/add',
        { product_id: productId, quantity },
        authHeader()
      );
      if (data.status) {
        toast.success('Added to cart!');
        setIsCartOpen(true);
      } else {
        toast.error(data.message || 'Could not add to cart.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add to cart.');
    }
  };

  // ══════════════════════════════════════
  // 3. INCREASE quantity (+1)
  //    PUT /cart/:cart_id  { quantity: newQty }
  // ══════════════════════════════════════
  const increaseQuantity = async (cartId, currentQty, stock) => {
    if (currentQty >= stock) {
      toast.warning('Stock limit reached!');
      return;
    }

    const newQty = currentQty + 1;

    // optimistic UI update
    setCartItems(prev =>
      prev.map(i => i.cart_id === cartId
        ? { ...i, quantity: newQty, total_price: i.price * newQty }
        : i
      )
    );

    try {
      const { data } = await api.put(
        `cart/${cartId}`,           // ✅ PUT /cart/:cart_id
        { quantity: newQty },       // ✅ naya total quantity bhejo
        authHeader()
      );
      if (!data.status) {
        toast.error(data.message || 'Could not update quantity.');
        await fetchCart(); // revert
      } else {
        await fetchCart();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update quantity.');
      await fetchCart(); // revert
    }
  };

  // ══════════════════════════════════════
  // 4. DECREASE quantity (-1)
  //    PUT /cart/:cart_id  { quantity: newQty }
  // ══════════════════════════════════════
  const decreaseQuantity = async (cartId, currentQty) => {
    if (currentQty <= 1) {
      removeFromCart(cartId);
      return;
    }

    const newQty = currentQty - 1;

    // optimistic UI update
    setCartItems(prev =>
      prev.map(i => i.cart_id === cartId
        ? { ...i, quantity: newQty, total_price: i.price * newQty }
        : i
      )
    );

    try {
      const { data } = await api.put(
        `cart/${cartId}`,           // ✅ PUT /cart/:cart_id
        { quantity: newQty },       // ✅ naya total quantity bhejo
        authHeader()
      );
      if (!data.status) {
        toast.error(data.message || 'Could not update quantity.');
        await fetchCart(); // revert
      } else {
        await fetchCart();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update quantity.');
      await fetchCart(); // revert
    }
  };

  // ══════════════════════════════════════
  // 5. REMOVE single item
  //    DELETE /cart/:cart_id
  // ══════════════════════════════════════
  const removeFromCart = async (cartId) => {
    setCartItems(prev => prev.filter(i => i.cart_id !== cartId));
    try {
      await api.delete(`cart/${cartId}`, authHeader());  // ✅ DELETE /cart/:cart_id
      toast.info('Removed from cart');
      await fetchCart();
    } catch (err) {
      toast.error('Failed to remove item.');
      await fetchCart();
    }
  };

  // ══════════════════════════════════════
  // 6. CLEAR all
  // ══════════════════════════════════════
  const clearCart = async () => {
    try {
      await api.delete('cart/clear', authHeader());
      setCartItems([]);
      toast.info('Cart cleared');
    } catch (err) {
      toast.error('Failed to clear cart.');
    }
  };

  // ── Totals ──
  const getCartTotal = () =>
    cartItems.reduce((t, i) => t + Number(i.price) * Number(i.quantity), 0);

  const getCartItemsCount = () =>
    cartItems.reduce((c, i) => c + Number(i.quantity), 0);

  // ── Wishlist (local) ──
  const addToWishlist = (product) => {
    setWishlistItems(prev => {
      if (prev.find(i => i.id === product.id)) {
        toast.info('Already in wishlist'); return prev;
      }
      toast.success('Added to wishlist!');
      return [...prev, product];
    });
  };
  const removeFromWishlist = (productId) => {
    setWishlistItems(prev => prev.filter(i => i.id !== productId));
    toast.info('Removed from wishlist');
  };
  const isInWishlist = (productId) =>
    wishlistItems.some(i => i.id === productId);

  const value = {
    cartItems,
    wishlistItems,
    isCartOpen,
    isLoading,
    setIsCartOpen,
    fetchCart,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    getCartTotal,
    getCartItemsCount,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;